// AdminPage - Refactored Version
// Main admin panel with modular tab components
import React, { useState, useEffect } from 'react';
import { Users, Shield, Key, Mail, Database, Cloud, GitMerge, Percent } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import GoogleIntegrationPanel from './GoogleIntegrationPanel';
import * as api from '../services/api';

// Import modular tab components
import UsersTab from './admin/UsersTab';
import RolesTab from './admin/RolesTab';
import ApiKeysTab from './admin/ApiKeysTab';
import TemplatesTab from './admin/TemplatesTab';
import BackupTab from './admin/BackupTab';
import DuplicatesTab from './admin/DuplicatesTab';
import CommissionsTab from './admin/CommissionsTab';

const AdminPage = ({ currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  
  // Data state
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, rolesRes, keysRes, templatesRes] = await Promise.all([
        api.getUsers(),
        api.getRoles(),
        api.getAPIKeys(),
        api.getEmailTemplates()
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data || []);
      setApiKeys(keysRes.data);
      setEmailTemplates(templatesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4A843]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Administración</h1>
          <p className="text-sm text-gray-500">Gestión de usuarios, roles, configuración y respaldos</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-8 w-full max-w-6xl">
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users size={16} />
            Usuarios
          </TabsTrigger>
          <TabsTrigger value="commissions" className="flex items-center gap-2" data-testid="commissions-tab">
            <Percent size={16} />
            Comisiones
          </TabsTrigger>
          <TabsTrigger value="roles" className="flex items-center gap-2">
            <Shield size={16} />
            Roles
          </TabsTrigger>
          <TabsTrigger value="api-keys" className="flex items-center gap-2">
            <Key size={16} />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="google" className="flex items-center gap-2">
            <Cloud size={16} />
            Google
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <Mail size={16} />
            Plantillas
          </TabsTrigger>
          <TabsTrigger value="duplicates" className="flex items-center gap-2" data-testid="duplicates-tab">
            <GitMerge size={16} />
            Duplicados
          </TabsTrigger>
          <TabsTrigger value="backup" className="flex items-center gap-2" data-testid="backup-tab">
            <Database size={16} />
            Respaldo
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          <UsersTab 
            users={users} 
            setUsers={setUsers} 
            roles={roles}
            currentUser={currentUser}
          />
        </TabsContent>

        {/* Commissions Tab */}
        <TabsContent value="commissions" className="mt-6">
          <CommissionsTab 
            users={users}
            currentUser={currentUser}
          />
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles" className="mt-6">
          <RolesTab 
            roles={roles} 
            setRoles={setRoles} 
          />
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="api-keys" className="mt-6">
          <ApiKeysTab 
            apiKeys={apiKeys} 
            setApiKeys={setApiKeys} 
          />
        </TabsContent>

        {/* Google Integration Tab */}
        <TabsContent value="google" className="mt-6">
          <GoogleIntegrationPanel currentUser={currentUser} isAdmin={true} />
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-6">
          <TemplatesTab 
            emailTemplates={emailTemplates} 
            setEmailTemplates={setEmailTemplates}
            currentUser={currentUser}
          />
        </TabsContent>

        {/* Duplicates Tab */}
        <TabsContent value="duplicates" className="mt-6">
          <DuplicatesTab />
        </TabsContent>

        {/* Backup Tab */}
        <TabsContent value="backup" className="mt-6">
          <BackupTab loadData={loadData} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;
